import * as React from 'react';
import * as _ from 'lodash';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, ExpertSystemActionCreators } from '@nkr/store/index';
import { IExpertSystemElementsResponse, IExpertSystemEpletRiskResponse, IExpertSystemOrderResponse, IExpertSystemWaitTimeResponse } from '@nkr/store/expert-system';
import { IBaseResponse, IExpertSystemElements, IExpertSystemOrder, IExpertSystemWaitTimes, ISingleEpletMmRisk } from '@nkr/models/index';
import { Risk } from './models';
import { Alert, Button, Glyphicon, Label, Table } from 'react-bootstrap';
import { ExpertAnalysisWrapper } from './styles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import SingleEpletMmRisk from '../swap-management/single-eplet-mm-risk';
import KflDirectDetails from './kfl-direct-details';
import AlleleMatch from '@nkr/components/allele-match';

interface IProps {
    centerId: number,
    order: IExpertSystemOrder,
    onReturnHome: () => void
};

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getEpletMismatchRiskByDonorRecipient: (donorId: number, recipientId: number) => Promise<IExpertSystemEpletRiskResponse>,
    getExpertSystemElements: (donorId: number, recipientId: number) => Promise<IExpertSystemElementsResponse>,
    getExpertSystemWaitTimes: (recipientId: number) => Promise<IExpertSystemWaitTimeResponse>,
    doCompatiblePair: (donorId: number, recipientId: number, direct: boolean, surgeryDate: string) => Promise<IBaseResponse>,
    doStandardVoucher: (donorId: number, recipientId: number) => Promise<IBaseResponse>,
    updateExpertSystemOrder: (orderId: number, statusId: number) => Promise<IExpertSystemOrderResponse>,
}

type TProps =
    IProps
    & IStateProps
    & IDispatchProps;

interface IState {
    waitTimeData: IExpertSystemWaitTimes,
    donorMismatchRisk: string,
    singleEpletMismatchRisk: ISingleEpletMmRisk,
    xAxis: number[],
    yAxis: number[],
    yLabels: string[],
    yWaitLabels: string[],
    options: any,
    recommendation: string,
    buttonClass: string,
    btnRisk: string,
    svBtnClass: string,
    kpdBtnClass: string,
    kidBtnClass: string,
    elements: IExpertSystemElements,
    // surgeryDate: string,
    showDetails: boolean
}

class ExpertSystemAnalysis extends React.Component<TProps, IState> {
    readonly state: IState = {
        waitTimeData: {
            recipientId: 0,
            totalAbocDonorsInLastYear: 0,
            estimatedAbocDonorsPerMonth: 0,
            totalAbocDonorsWith3gTyping: 0,
            countEpletMmNone: 0,
            countEpletMmLow: 0,
            countEpletMmIntermediate: 0,
            countEpletMmHigh: 0,
            cpraEpletMmNone: 0,
            cpraEpletMmLow: 0,
            cpraEpletMmIntermediate: 0,
            donorsNeededMmNone: 0,
            donorsNeededMmLow: 0,
            donorsNeededMmIntermediate: 0,
            estimatedWaitMmIntermediate: 0,
            estimatedWaitMmLow: 0,
            estimatedWaitMmNone: 0,
        },
        donorMismatchRisk: '',
        singleEpletMismatchRisk: null,
        xAxis: [],
        yAxis: [],
        yLabels: [],
        yWaitLabels: [],
        options: {},
        recommendation: '',
        buttonClass: '',
        btnRisk: '',
        svBtnClass: '',
        kpdBtnClass: '',
        kidBtnClass: '',
        elements: {
            donorAbo: '',
            recipientAbo: '',
            bmtMm: null,
            recipientCpra: '',
            recipientDialysisStatus: '',
            isInSwap: false,
            isAboCompatible: true
        },
        // surgeryDate: '',
        showDetails: false
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let { donorId, recipientId } = this.props.order;

        this.props.showLoading('Getting expert system order details, one moment please...').then(() => {
            Promise.all([
                this.props.getEpletMismatchRiskByDonorRecipient(donorId, recipientId),
                this.props.getExpertSystemWaitTimes(recipientId),
                this.props.getExpertSystemElements(donorId, recipientId)
            ]).then(([riskResponse, waitResponse, elementsResponse]) => {
                let success = riskResponse.data.success && waitResponse.data.success && elementsResponse.data.success;
                let wait = waitResponse.data.results;
                let singleEpletMismatchRisk = riskResponse.data.results;
                let elements = elementsResponse.data.results;
                let xAxis = [0, 1, 2, 3, 4, 5, 6];
                let yAxis = [];
                let epletValue = 0;
                let yLabels = ['High', 'Medium', 'Low', '0 MM'];
                let recommendation = '';
                let buttonClass = 'btn btn-xs btn-danger';
                let btnRisk = 'UNK';
                let svBtnClass = 'btn-default';
                let kpdBtnClass = 'btn-default';
                let kidBtnClass = 'btn-default';
                let risk = '';
                let lowWaitMonths = wait.estimatedWaitMmLow > 60 ? '60+' : wait.estimatedWaitMmLow;
                let noneWaitMonths = wait.estimatedWaitMmNone > 60 ? '60+' : wait.estimatedWaitMmNone;

                if (singleEpletMismatchRisk.singleEpletRisk == Risk.Zero) {
                    risk = 'zero';
                }

                if (singleEpletMismatchRisk.singleEpletRisk == Risk.Low) {
                    risk = 'low';
                }

                if (singleEpletMismatchRisk.singleEpletRisk == Risk.Intermediate) {
                    risk = 'int';
                }

                if (singleEpletMismatchRisk.singleEpletRisk == Risk.High) {
                    risk = 'high';
                }

                if (singleEpletMismatchRisk.singleEpletRisk == Risk.NoData) {
                    risk = 'unknown';
                }

                if (success) {
                    if (risk == 'zero') {
                        btnRisk = '0 MM'
                        buttonClass = 'btn btn-xs btn-success';
                        recommendation = 'Unless this donor is incompatible, or there is another reason why this donor should not donate to this recipient, we recommend that you proceed with KFL internal direct donation so that the donor gets the living kidney prioritization protection. ';
                        kidBtnClass = 'btn-success';
                    }

                    if (risk == 'low') {
                        buttonClass = 'btn btn-xs btn-success';
                        btnRisk = 'Low'

                        if (wait.estimatedWaitMmNone <= 3) {
                            recommendation = 'It is expected that the wait time for a zero eplet mismatch donor is ' + wait.estimatedWaitMmNone + ' months. If this donor is compatible, we recommend that you proceed with NKR KPD as a compatible pair. If this donor is not compatible, we recommend that you proceed with NKR KPD as a standard voucher pair.';
                            kpdBtnClass = 'btn-success';
                        } else {
                            recommendation = 'This recipient is not expected to see a zero eplet mismatch donor for ' + noneWaitMonths + ' months. Unless this donor is incompatible, or there is another reason why this donor should not donate to this recipient, we recommend that you proceed with NKR internal direct donation so that the donor gets the living kidney prioritization protection.';
                            kidBtnClass = 'btn-success';
                        }
                    }

                    if (risk == 'int') {
                        btnRisk = 'Intermediate'
                        buttonClass = 'btn btn-xs btn-warning';

                        if (wait.estimatedWaitMmLow <= 3) {
                            recommendation = 'It is expected that the wait time for a low eplet mismatch donor is ' + wait.estimatedWaitMmLow + ' months. We recommend that you proceed with NKR KPD as a standard voucher pair.';
                            svBtnClass = 'btn-success';
                        } else if (wait.estimatedWaitMmLow <= 4) {
                            recommendation = 'It is expected that the wait time for a low eplet mismatch donor is ' + wait.estimatedWaitMmLow + ' months. We recommend that you proceed with NKR KPD as a compatible pair.';
                            kpdBtnClass = 'btn-success';
                        } else {
                            recommendation = 'This recipient is not expected to see a low eplet mismatch donor for ' + lowWaitMonths + ' months. Unless this donor is incompatible, or there is another reason why this donor should not donate to this recipient, we recommend that you proceed with NKR internal direct donation so that the donor gets the living kidney prioritization protection.';
                            kidBtnClass = 'btn-success';
                        }
                    }

                    if (risk == 'high') {
                        btnRisk = 'High'
                        buttonClass = 'btn btn-xs btn-danger';

                        if (wait.estimatedWaitMmLow <= 4) {
                            recommendation = 'It is expected that the wait time for a low eplet mismatch donor is ' + wait.estimatedWaitMmLow + ' months. We recommend that you proceed with NKR KPD as a standard voucher pair.';
                            svBtnClass = 'btn-success';
                        } else if (wait.estimatedWaitMmLow <= 5) {
                            recommendation = 'It is expected that the wait time for a low eplet mismatch donor is ' + wait.estimatedWaitMmLow + ' months. We recommend that you proceed with NKR KPD as a compatible pair.';
                            kpdBtnClass = 'btn-success';
                        } else {
                            recommendation = 'This recipient is not expected to see a low eplet mismatch donor for ' + lowWaitMonths + ' months. Unless this donor is incompatible, or there is another reason why this donor should not donate to this recipient, we recommend that you proceed with NKR internal direct donation so that the donor gets the living kidney prioritization protection.';
                            kidBtnClass = 'btn-success';
                        }
                    }

                    if (!elements.isAboCompatible) {
                        recommendation = 'Decouple this pair due to blood type incompatibility. Proceed with a Standard Voucher donation.';
                        svBtnClass = 'btn-success';
                        kpdBtnClass = 'btn-default';
                        kidBtnClass = 'btn-default';
                    }

                    for (let step = 0; step <= 6; step++) {
                        if (epletValue < 1 && step == wait.estimatedWaitMmIntermediate) {
                            epletValue = 1;
                        }

                        if (epletValue < 2 && step == wait.estimatedWaitMmLow) {
                            epletValue = 2;
                        }

                        if (epletValue < 3 && step == wait.estimatedWaitMmNone) {
                            epletValue = 3;
                        }

                        yAxis.push(epletValue);
                    }

                    let options = {
                        chart: {
                            type: 'spline',
                            events: {
                                load() {
                                    this.showLoading();
                                    setTimeout(this.hideLoading.bind(this), 2000);
                                }
                            },
                            borderColor: '#ddd',
                            borderRadius: 4,
                            borderWidth: 2
                        },
                        title: { text: '<b style=\'font-size:22px;\'>Estimated Wait Time</b>' },
                        xAxis: [{
                            categories: xAxis,
                            crosshair: true,
                            title: { text: 'Months' }
                        }],
                        plotOptions: {
                            spline: {
                                lineWidth: 4,
                                states: {
                                    hover: {
                                        lineWidth: 5
                                    }
                                },
                                marker: {
                                    enabled: false
                                },
                            }
                        },
                        yAxis: [{ // Primary yAxis
                            categories: yLabels,
                            labels: {
                                style: {
                                    color: '#434348'
                                }
                            },
                            title: {
                                text: 'Eplet MM Stratification',
                                style: {
                                    color: '#434348'
                                }
                            },
                            max: 3
                        }],
                        tooltip: {
                            formatter: function () {
                                if (this.y == 1) {
                                    return 'It is estimated that you will encounter a donor with an <b>Intermediate Eplet MM</b> every ' + wait.estimatedWaitMmIntermediate + ' month(s).';
                                }

                                if (this.y == 2) {
                                    return 'It is estimated that you will encounter a donor with a <b>Low Eplet MM</b> every ' + wait.estimatedWaitMmLow + ' month(s).';
                                }

                                if (this.y == 3) {
                                    return 'It is estimated that you will encounter a donor with a <b>0 Eplet MM</b> every ' + wait.estimatedWaitMmNone + ' month(s).';
                                }

                                return 'You will not encounter any Intermediate, Low or 0 eplet MM donors at ' + this.x + ' month(s).';
                            }
                            /*formatter: function() {
                                return this.state.yWaitLabels[this.y];
                            }*/
                        },
                        legend: {
                            enabled: false
                        },
                        series: [{
                            name: '',
                            data: yAxis,
                        }],
                        loading: {
                            labelStyle: { 'fontSize': '24px' }
                        },
                        lang: {
                            loading: 'One moment please...'
                        }
                    };

                    this.setState({
                        waitTimeData: wait,
                        donorMismatchRisk: risk,
                        yWaitLabels: ['',
                            'It is estimated that you will encounter a donor with an <b>Intermediate Eplet MM</b> every ' + wait.estimatedWaitMmIntermediate + ' months',
                            'It is estimated that you will encounter a donor with a <b>Low Eplet MM</b> every ' + wait.estimatedWaitMmLow + ' months',
                            'It is estimated that you will encounter a donor with a <b>0 Eplet MM</b> every ' + wait.estimatedWaitMmNone + ' months',
                        ],
                        xAxis,
                        yAxis,
                        yLabels,
                        options,
                        recommendation,
                        buttonClass,
                        btnRisk,
                        singleEpletMismatchRisk,
                        svBtnClass,
                        kpdBtnClass,
                        kidBtnClass,
                        elements,
                    }, () => {
                        this.props.hideLoading();
                    });
                } else {
                    this.props.hideLoading();
                }
            })
        });
    }

    handleOpenReport = () => {
        let { donorId, recipientId } = this.props.order;

        let url = __OUTSIDEURL__ + '/centers/recipient/eplet-mismatch?id=' + recipientId + '&donor=' + donorId;
        let win = window.open(url, '_blank');

        win.focus();
    }

    handleWithdrawDonor = () => {
        if (confirm('Are you sure you wish to withdraw this donor?')) {
            this.props.showLoading('Clearing expert system order one moment please...').then(() => {
                this.props.updateExpertSystemOrder(this.props.order.expertSystemId, 0).then((response) => {
                    if (response.data.success) {
                        this.props.hideLoading();
                        window.location.href = '/center/' + this.props.centerId + '/action-center/expert-system/';
                    } else {
                        this.props.hideLoading();
                        alert('An error has occurred...');
                    }
                });
            });
        }
    };

    handleStandardVoucher = () => {
        let { donorAlias, recipientAlias } = this.props.order;

        let text = 'Are you sure that you want to convert Donor: ' + donorAlias + ' and recipient ' + recipientAlias + ' to a standard voucher pair?';

        let answer = confirm(text);

        if (answer) {
            let { expertSystemId, donorId, recipientId } = this.props.order;

            this.props.updateExpertSystemOrder(expertSystemId, 7).then(updateResponse => {
                this.props.doStandardVoucher(donorId, recipientId).then(response => {
                    if (response.data.success) {
                        alert('This pair has been converted to a standard voucher pair. Please enter the remaining data on the profile. ');

                        //Redirect to recipient?
                        let url = `center/${this.props.centerId}/recipient/${recipientId}/profile/general`;
                        let win = window.open(url, '_blank');

                        win.focus();
                    } else {
                        alert('There was an error performing the operation. Please contact your liaison. ');
                    }
                });
            });
        }
    }

    handleKpdPair = () => {
        let { donorAlias, recipientAlias } = this.props.order;

        let text = 'Are you sure that you want to convert Donor: ' + donorAlias + ' and recipient ' + recipientAlias + ' to a compatible KPD pair?';

        let answer = confirm(text);

        if (answer) {
            let { expertSystemId, donorId, recipientId } = this.props.order;

            this.props.updateExpertSystemOrder(expertSystemId, 7).then(updateResponse => {
                this.props.doCompatiblePair(donorId, recipientId, false, '').then(response => {
                    if (response.data.success) {
                        alert('This pair has been converted to a compatible KPD pair. Please enter the remaining data on the profile. ');

                        let url = `center/${this.props.centerId}/recipient/${recipientId}/profile/general`;
                        let win = window.open(url, '_blank');

                        win.focus();
                        //Redirect to recipient?
                    } else {
                        alert('There was an error performing the operation. Please contact your liaison. ');
                    }
                });
            });
        }
    }

    handleDonorPreSelect = () => {
        let { recipientId } = this.props.order;
        let url = '/center/' + this.props.centerId + '/recipient/' + recipientId + '/potential-donors';
        let win = window.open(url, '_blank');
        win.focus();
    }

    handleInternalDirect = () => {
        this.setState({
            showDetails: true
        });
    }

    handleCloseDetails = () => {
        this.setState({
            showDetails: false
        });
    }

    handleSubmitDetails = (surgeryDate: string) => {
        let { expertSystemId, donorId, recipientId } = this.props.order;

        this.setState({
            showDetails: false
        }, () => {
            this.props.doCompatiblePair(donorId, recipientId, true, surgeryDate).then(response => {
                if (response.data.success) {
                    this.props.updateExpertSystemOrder(expertSystemId, 7).then(updateResponse => {
                        alert('This pair has been converted to an Internal Direct pair. Please enter the remaining data on the profile. ');

                        let url = `center/${this.props.centerId}/recipient/${recipientId}/profile/general`;
                        let win = window.open(url, '_blank');

                        win.focus();
                        //Redirect to recipient?
                    });
                } else {
                    alert(response.data.message || 'There was an error performing the operation. Please contact your liaison. ');
                }
            });
        });
    }

    render = () => {
        if (this.state.donorMismatchRisk == '') {
            return <div></div>;
        }

        const { donorAlias, recipientAlias } = this.props.order;
        const { donorMismatchRisk: risk, waitTimeData: wait, options, recommendation, svBtnClass, kpdBtnClass, kidBtnClass, elements, singleEpletMismatchRisk } = this.state;

        return (
            <>
                <ExpertAnalysisWrapper>
                    <div style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
                        <div className='container'>
                            <div className='row' style={{ marginBottom: 20 }}>
                                {this.state.elements.isInSwap && (
                                    <div className='col-xs-12'>
                                        <Alert bsStyle='warning' bsSize='sm'>The donor and recipient are currently in a swap.</Alert>
                                    </div>
                                )}

                                {!elements.isAboCompatible && (
                                    <div className='col-xs-12'>
                                        <Alert bsStyle='warning' bsSize='sm'>
                                            <b>Blood type incompatibility detected</b><br />
                                            Donor ({elements.donorAbo}) and recipient ({elements.recipientAbo}) are incompatible. This pair cannot proceed with an Internal Direct donation.
                                        </Alert>
                                    </div>
                                )}

                                {/* {this.state.elements.bmtMm == -1 && (
                                    <div className='col-xs-12'>
                                        <Alert bsStyle='warning' bsSize='sm'>We are missing some data...</Alert>
                                    </div>
                                )} */}

                                <div className='col-xs-12'>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h1 className='my-0'>Evaluate Donor {donorAlias} for Recipient {recipientAlias}</h1>
                                        <Button bsStyle='link' onClick={this.props.onReturnHome}>Return to Expert System DashBoard</Button>
                                    </div>
                                </div>

                                <div className='col-md-5 col-lg-6 mt-5'>
                                    <p>The eplet mismatch between <b>donor {donorAlias}</b> and <b>recipient {recipientAlias}</b> is&nbsp;&nbsp; {elements.isAboCompatible ? <SingleEpletMmRisk data={singleEpletMismatchRisk} isAdmin={false} centerId={this.props.centerId} placement='bottom' width='50px' height='30px' /> : <Label bsStyle='inverse' style={{fontSize: '100%'}}> ABOi</Label>}</p>
                                    <p>{recommendation}</p>
                                </div>

                                <div className='col-sm-7 col-md-4 col-lg-4 mt-5 col-table'>
                                    <Table condensed style={{ marginBottom: 0 }}>
                                        <tbody>
                                            <tr>
                                                <th>Donor Blood Type:</th>
                                                <td>{elements.donorAbo}</td>
                                            </tr>
                                            <tr>
                                                <th>Recipient Blood Type:</th>
                                                <td>{elements.recipientAbo}</td>
                                            </tr>
                                            <tr>
                                                <th>Allele Match:</th>
                                                <td>{elements.bmtMm != null ? <><AlleleMatch bmtMm={elements.bmtMm} displayAsInline={false} /></> : <>N/A</>}</td>
                                            </tr>
                                            {!elements.isAboCompatible && (
                                                <tr>
                                                    <th>Eplet Mismatch:</th>
                                                    <td><Label bsStyle='inverse'>ABOi</Label></td>
                                                </tr>
                                            )}
                                            <tr>
                                                <th>Recipient PRA:</th>
                                                <td>{elements.recipientCpra}</td>
                                            </tr>
                                            <tr>
                                                <th>Recipient Dialysis Status:</th>
                                                <td>{elements.recipientDialysisStatus}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>

                                <div className='col-sm-5 col-md-3 col-lg-2 mt-5 col-buttons'>
                                    {this.state.elements.isInSwap == false && (
                                        <>
                                            <Button block style={{ marginBottom: 1 }} onClick={this.handleStandardVoucher} className={svBtnClass}>Start Standard Voucher</Button>
                                            <Button block style={{ marginBottom: 1 }} onClick={this.handleKpdPair} className={kpdBtnClass}>Start KPD Pair</Button>
                                            <Button block style={{ marginBottom: 1 }} onClick={this.handleInternalDirect} className={kidBtnClass} disabled={!elements.isAboCompatible} title={elements.isAboCompatible ? undefined : 'Blood type incompatible pairs cannot proceed with an Internal Direct donation'}>
                                                Start Internal Direct{!elements.isAboCompatible && <>&nbsp;<Glyphicon glyph='lock' /></>}
                                            </Button>
                                        </>
                                    )}
                                    <Button block style={{ marginBottom: 1 }} onClick={this.handleDonorPreSelect}>Pre-Selects</Button>
                                    <Button block onClick={this.handleWithdrawDonor}>Withdraw Donor</Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
                        <div className='container'>
                            <div className='row'>
                                <div className='col-sm-12'>
                                    {this.state.elements.bmtMm == null ?
                                        <Alert bsStyle='warning' bsSize='sm' style={{ margin: 0 }}>We are unable to show the EPLET MISMATCH information due to missing data...</Alert>
                                        :
                                        <>
                                            <p>
                                                Below is a chart with expected wait times based on your patients eplet information to find various eplet mismatch donors.
                                            </p>

                                            <HighchartsReact highcharts={Highcharts} options={options} />

                                            <p style={{ marginTop: '3rem' }}>It is estimated that you will encounter a donor with:</p>
                                            <span className='tag green'>a <b>0 Eplet MM</b> every {wait.estimatedWaitMmNone} month(s)</span>
                                            <span className='tag yellow'>a <b>Low Eplet MM</b> every {wait.estimatedWaitMmLow} month(s)</span>
                                            <span className='tag orange'>a <b>Medium Eplet MM</b> every {wait.estimatedWaitMmIntermediate} month(s)</span>
                                        </>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
                        <div className='container'>
                            <div className='row'>
                                <div className='col-xs-12'>
                                    <h2 style={{ marginTop: 0 }}>Kidney for Life Wait Times for Compatible Pairs</h2>
                                    <img style={{ marginBottom: 20, marginTop: 10, border: '1px solid #ccc', borderRadius: 4 }} width='100%' src='/images/expert-system/kfl-wait-times_5_3_2024.png' />
                                </div>
                            </div>
                        </div>
                    </div>
                </ExpertAnalysisWrapper>

                <KflDirectDetails
                    show={this.state.showDetails}
                    recipientAlias={this.props.order.recipientAlias}
                    donorAlias={this.props.order.donorAlias}
                    onSubmit={this.handleSubmitDetails}
                    onClose={this.handleCloseDetails}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getEpletMismatchRiskByDonorRecipient: (donorId, recipientId) => {
            return dispatch(ExpertSystemActionCreators.getEpletMismatchRiskByDonorRecipient(donorId, recipientId));
        },
        getExpertSystemElements: (donorId, recipientId) => {
            return dispatch(ExpertSystemActionCreators.getExpertSystemElements(donorId, recipientId));
        },
        getExpertSystemWaitTimes: (recipientId) => {
            return dispatch(ExpertSystemActionCreators.getExpertSystemWaitTimes(recipientId));
        },
        doCompatiblePair: (donorId, recipientId, direct, surgeryDate) => {
            return dispatch(ExpertSystemActionCreators.doCompatiblePair(donorId, recipientId, direct, surgeryDate));
        },
        doStandardVoucher: (donorId, recipientId) => {
            return dispatch(ExpertSystemActionCreators.doStandardVoucher(donorId, recipientId));
        },
        updateExpertSystemOrder: (orderId, statusId) => {
            return dispatch(ExpertSystemActionCreators.updateExpertSystemOrder(orderId, statusId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(ExpertSystemAnalysis)